import { HttpClient } from '@wix/http-client';
import { queryIdentityFactors } from '@wix/ambassador-identity-factors-v1-identity-factor/http';
import {
  Status,
  FactorType,
} from '@wix/ambassador-identity-factors-v1-identity-factor/types';
import {
  acrKey,
  StepUpCancelledError,
  StepUpFailedError,
  type StepUpManager,
  type StepUpRequiredDetails,
} from '@wix/stepup';
import {
  CreatePasskeyErrorCode,
  type WebAuthnEnrollmentStore,
} from '@wix/webauthn-management';

export const PASSKEY_ENROLL_STEP_UP_MAX_AGE_SECONDS = 300;
// acr values are opaque strings to @wix/stepup; every client keeps its own copy.
export const STEP_UP_ACR_MFA = 'mfa';
export const STEP_UP_ACR_CREDENTIALS = 'credentials';

// The first step-up uses our guess of the acr; one more is allowed with exactly what
// the server asked for. Past that, a rejection is not something another step-up fixes
// — and when the silent attempt keeps succeeding there is no UI for the user to
// cancel, so retrying forever would hang the screen.
const MAX_STEP_UPS = 2;

type StepUpEnrollmentStore = Pick<
  WebAuthnEnrollmentStore,
  'setStepUpAccessToken' | 'enrollWebAuthn'
>;
type StepUpTokens = Pick<
  StepUpManager,
  'promptStepUp' | 'stepUp' | 'evictToken'
>;

export type PasskeyStepUpEnrollOutcome =
  | { success: true; isBackedUp: boolean }
  // User dismissed the step-up modal: quiet abort, not an error.
  | { success: false; cancelled: true }
  | {
      success: false;
      cancelled?: false;
      errorCode: CreatePasskeyErrorCode;
      errorMessage: string;
    };

// `mfa` if the user has an active non-PASSWORD factor, else `credentials` (also when
// the lookup fails; the server can still ask for more).
export async function resolveEnrollAcr(
  httpClient: HttpClient = new HttpClient(),
): Promise<string> {
  try {
    const { data } = await httpClient.request(queryIdentityFactors({}));
    const hasActiveSecondFactor = (data?.identityFactors ?? []).some(
      (factor) =>
        factor.status === Status.ACTIVE &&
        !!factor.type &&
        factor.type !== FactorType.PASSWORD,
    );
    return hasActiveSecondFactor ? STEP_UP_ACR_MFA : STEP_UP_ACR_CREDENTIALS;
  } catch {
    return STEP_UP_ACR_CREDENTIALS;
  }
}

// Steps up before enrolling rather than on the server's 403: retrying a rejected
// enroll call would re-run the browser's passkey prompt.
export async function preMintStepUpAndEnroll(deps: {
  enrollmentStore: StepUpEnrollmentStore;
  stepUpManager: StepUpTokens;
  httpClient?: HttpClient;
}): Promise<PasskeyStepUpEnrollOutcome> {
  const { enrollmentStore, stepUpManager } = deps;
  const acr = await resolveEnrollAcr(deps.httpClient);
  let requirement: StepUpRequiredDetails = {
    acrValues: [acr],
    maxAge: PASSKEY_ENROLL_STEP_UP_MAX_AGE_SECONDS,
  };

  try {
    let token = await stepUpManager.promptStepUp(
      acr,
      PASSKEY_ENROLL_STEP_UP_MAX_AGE_SECONDS,
    );

    for (let stepUps = 1; ; stepUps++) {
      enrollmentStore.setStepUpAccessToken(token);
      const result = await enrollmentStore.enrollWebAuthn();

      if (result.success) {
        return { success: true, isBackedUp: result.isBackedUp };
      }
      if (
        result.errorCode !== CreatePasskeyErrorCode.STEP_UP_REQUIRED ||
        stepUps >= MAX_STEP_UPS
      ) {
        return {
          success: false,
          errorCode: result.errorCode,
          errorMessage: result.errorMessage,
        };
      }

      // Never hand the rejected token out again, and mint fresh for what the server
      // actually requires (stepUp skips the cache, unlike promptStepUp).
      stepUpManager.evictToken(acrKey(requirement.acrValues), token);
      requirement = result.stepUpRequirement ?? requirement;
      token = await stepUpManager.stepUp(
        acrKey(requirement.acrValues),
        requirement,
      );
    }
  } catch (error) {
    if (error instanceof StepUpCancelledError) {
      return { success: false, cancelled: true };
    }
    const errorMessage =
      error instanceof StepUpFailedError
        ? `Step-up authentication failed: ${error.errorCode}`
        : `Step-up authentication error: ${String(error)}`;
    return {
      success: false,
      errorCode: CreatePasskeyErrorCode.GENERAL,
      errorMessage,
    };
  }
}
